import type { MonthMarker, QuarterMarker } from '@/types/timeline';

export interface TimelineHeaderProps {
  /** Width of the chart area (excluding label column) */
  chartWidth: number;
  /** Month markers to display (bottom row) */
  monthMarkers: MonthMarker[];
  /** Quarter markers to display (top row) */
  quarterMarkers: QuarterMarker[];
  /** Current zoom level (default: 1.0) */
  zoom?: number;
}

// Layout constants for two-layer header
const QUARTER_ROW_HEIGHT = 24;
const MONTH_ROW_HEIGHT = 20;
const TOTAL_HEADER_HEIGHT = QUARTER_ROW_HEIGHT + MONTH_ROW_HEIGHT;

/**
 * Timeline header molecule - renders two-layer time scale
 *
 * Top row: Quarter labels (Q1 2026, Q2 2026, etc.) centered over their span
 * Bottom row: Month numbers (1, 2, 3, etc.) centered in each month
 *
 * Supports zoom scaling: positions are multiplied by zoom level.
 */
export function TimelineHeader({ chartWidth, monthMarkers, quarterMarkers, zoom = 1 }: TimelineHeaderProps) {
  const scaledWidth = chartWidth * zoom;

  // Calculate center X for a month label (between its gridline and the next)
  const getMonthLabelX = (marker: MonthMarker, index: number): number => {
    const currentX = marker.x * zoom;
    const nextX = index < monthMarkers.length - 1 ? monthMarkers[index + 1]!.x * zoom : scaledWidth;
    return (currentX + nextX) / 2;
  };

  return (
    <svg
      width={scaledWidth}
      height={TOTAL_HEADER_HEIGHT}
      className="shrink-0 font-sans"
      style={{ minHeight: TOTAL_HEADER_HEIGHT }}
    >
      {/* Top row: Quarter labels */}
      <g>
        {quarterMarkers.map((quarter, i) => {
          const startX = quarter.startX * zoom;
          const endX = quarter.endX * zoom;
          const centerX = (startX + endX) / 2;
          const width = endX - startX;

          return (
            <g key={`q-${quarter.year}-${quarter.quarter}`}>
              {/* Quarter background - subtle alternating, spans both rows */}
              <rect
                x={startX}
                y={0}
                width={width}
                height={TOTAL_HEADER_HEIGHT}
                className={i % 2 === 0 ? 'fill-bg-subtle' : 'fill-transparent'}
                fillOpacity={i % 2 === 0 ? 0.3 : 0}
              />
              {/* Quarter separator line */}
              <line
                x1={startX}
                y1={0}
                x2={startX}
                y2={QUARTER_ROW_HEIGHT}
                className="stroke-border-default"
                strokeWidth={1}
              />
              {/* Quarter label - centered */}
              {width > 40 && ( // Only show label if enough space
                <text
                  x={centerX}
                  y={QUARTER_ROW_HEIGHT / 2 + 4}
                  className="fill-text-secondary"
                  fontSize={11}
                  fontWeight={500}
                  textAnchor="middle"
                >
                  {quarter.label}
                </text>
              )}
            </g>
          );
        })}
        {/* Right edge line for last quarter */}
        <line
          x1={scaledWidth}
          y1={0}
          x2={scaledWidth}
          y2={QUARTER_ROW_HEIGHT}
          className="stroke-border-default"
          strokeWidth={1}
        />
      </g>

      {/* Separator line between rows */}
      <line
        x1={0}
        y1={QUARTER_ROW_HEIGHT}
        x2={scaledWidth}
        y2={QUARTER_ROW_HEIGHT}
        className="stroke-border-default"
        strokeWidth={1}
      />

      {/* Bottom row: Month labels */}
      <g>
        {monthMarkers.map((marker, i) => {
          const scaledX = marker.x * zoom;
          const labelX = getMonthLabelX(marker, i);
          const isQuarterStart = marker.date.getMonth() % 3 === 0;

          return (
            <g key={i}>
              {/* Month border - stronger for quarter boundaries */}
              <line
                x1={scaledX}
                y1={QUARTER_ROW_HEIGHT}
                x2={scaledX}
                y2={TOTAL_HEADER_HEIGHT}
                className={isQuarterStart ? 'stroke-border-default' : `stroke-border-subtle`}
                strokeWidth={1}
              />
              {/* Month number label - centered in month span */}
              <text
                x={labelX}
                y={QUARTER_ROW_HEIGHT + MONTH_ROW_HEIGHT / 2 + 4}
                className="fill-text-muted"
                fontSize={10}
                textAnchor="middle"
              >
                {marker.label}
              </text>
            </g>
          );
        })}
        {/* Right edge border for last month */}
        <line
          x1={scaledWidth}
          y1={QUARTER_ROW_HEIGHT}
          x2={scaledWidth}
          y2={TOTAL_HEADER_HEIGHT}
          className="stroke-border-subtle"
          strokeWidth={1}
        />
      </g>
    </svg>
  );
}

// Export the header height for use in parent components
export { TOTAL_HEADER_HEIGHT as TIMELINE_HEADER_HEIGHT };
